/**
 * CartContext — Gestion du panier ABBUSINESS
 *
 * Stratégie :
 *  - Guest    → panier en localStorage uniquement
 *  - Auth     → panier synchronisé avec l'API (server-side)
 *  - Connexion → merge localStorage → serveur via POST /cart/sync
 */
import {
  createContext, useContext, useReducer, useEffect,
  useCallback, useRef,
} from 'react'
import { cartApi } from '../services/api'
import { liste } from '../utils/listes'
import { useAuth } from './AuthContext'

const CartContext = createContext(null)

const LS_KEY = 'abbusiness_cart'

/* ──────────────────────────────────────────────
   State & Reducer
────────────────────────────────────────────── */
const initial = { items: [], loading: false, syncing: false, open: false, error: null }

function reducer(state, { type, payload }) {
  switch (type) {
    case 'SET_ITEMS':    return { ...state, items: payload, loading: false, syncing: false, error: null }
    case 'SET_ERROR':    return { ...state, error: payload, loading: false, syncing: false }
    case 'SET_LOADING':  return { ...state, loading: payload }
    case 'SET_SYNCING':  return { ...state, syncing: payload }
    case 'OPEN_CART':    return { ...state, open: true }
    case 'CLOSE_CART':   return { ...state, open: false }
    case 'TOGGLE_CART':  return { ...state, open: !state.open }
    default:             return state
  }
}

/* ──────────────────────────────────────────────
   Helpers localStorage (guest)
────────────────────────────────────────────── */
function lsGet() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) ?? [] } catch { return [] }
}
function lsSet(items) {
  localStorage.setItem(LS_KEY, JSON.stringify(items))
}
function lsClear() {
  localStorage.removeItem(LS_KEY)
}

/* ──────────────────────────────────────────────
   Provider
────────────────────────────────────────────── */
export function CartProvider({ children }) {
  const { isAuth, isLoading: authLoading } = useAuth()
  const [state, dispatch] = useReducer(reducer, initial)

  /*
   | Deux garde-fous contre les mauvaises ecritures de localStorage.
   |
   | hydratedRef : tant que le panier de la session courante n'a pas ete
   | charge, l'effet de persistance ne doit rien ecrire — sinon, a la
   | deconnexion, il recopiait le panier serveur de l'utilisateur precedent
   | dans le stockage local, d'ou il migrait ensuite dans le panier du suivant.
   |
   | sessionRef : identifie la session (auth vs invite) pour ignorer la
   | reponse d'une requete lancee avant un changement d'etat.
   */
  const hydratedRef = useRef(false)
  const sessionRef  = useRef(0)

  /*
   | Chargement du panier, fusion comprise.
   |
   | Auparavant deux effets concurrents partaient en parallele : GET /cart
   | (panier d'avant fusion) et POST /cart/sync (panier fusionne). Sur une
   | connexion lente, la reponse du GET pouvait arriver en dernier et effacer
   | visuellement les articles ajoutes en invite. Le client les re-ajoutait,
   | d'ou des quantites doublees.
   |
   | La fusion et le chargement sont desormais sequentiels et exclusifs.
   */
  useEffect(() => {
    if (authLoading) return

    const session = ++sessionRef.current
    hydratedRef.current = false

    if (!isAuth) {
      dispatch({ type: 'SET_ITEMS', payload: lsGet() })
      hydratedRef.current = true
      return
    }

    const guestItems = lsGet()

    const load = async () => {
      dispatch({ type: 'SET_LOADING', payload: true })

      try {
        let data

        if (guestItems.length > 0) {
          dispatch({ type: 'SET_SYNCING', payload: true })

          /*
           | Le stockage local est vide AVANT l'appel.
           |
           | Le backend additionne les quantites recues a celles deja en base.
           | Si la reponse se perdait (coupure, cas courant ici) alors que le
           | serveur avait bien enregistre, le rechargement suivant renvoyait
           | les memes lignes et doublait les quantites. En cas d'echec reel,
           | on restaure ce qu'on vient de retirer.
           */
          lsClear()

          try {
            data = await cartApi.sync(
              guestItems.map(i => ({
                product_id: i.product_id,
                variant_id: i.variant_id ?? null,
                quantity:   i.quantity,
              }))
            )
          } catch (err) {
            lsSet(guestItems)
            throw err
          }
        } else {
          data = await cartApi.get()
        }

        // Une session plus recente a demarre : cette reponse est perimee.
        if (session !== sessionRef.current) return

        dispatch({ type: 'SET_ITEMS', payload: liste(data.items, 'panier') })
      } catch (err) {
        if (session !== sessionRef.current) return

        dispatch({ type: 'SET_ITEMS', payload: [] })
        dispatch({ type: 'SET_ERROR', payload: err?.message ?? 'Panier indisponible.' })
      } finally {
        if (session === sessionRef.current) {
          hydratedRef.current = true
          dispatch({ type: 'SET_LOADING', payload: false })
          dispatch({ type: 'SET_SYNCING', payload: false })
        }
      }
    }

    load()
  }, [isAuth, authLoading])

  /* ── Persistance locale, uniquement en invite et une fois hydrate ── */
  useEffect(() => {
    if (!isAuth && !authLoading && hydratedRef.current) {
      lsSet(state.items)
    }
  }, [state.items, isAuth, authLoading])

  /* ── Actions ── */

  const addToCart = useCallback(async (product, quantity = 1, extras = {}) => {
    if (isAuth) {
      try {
        const data = await cartApi.add(product.id, quantity, extras)
        dispatch({ type: 'SET_ITEMS', payload: liste(data.items, 'panier') })
      } catch (err) {
        throw err
      }
    } else {
      /*
       | Invité — mise à jour optimiste du stockage local.
       |
       | La déclinaison choisie était perdue : un même produit en deux tailles
       | fusionnait en une seule ligne, et la fusion au moment de la connexion
       | envoyait le produit sans sa variante.
       */
      const variantId = extras.variant_id ?? null

      const available = variantId
        ? (product.variants?.find(v => v.id === variantId)?.stock ?? 1)
        : (product.stock ?? 1)

      const unitPrice = extras.unit_price
        ?? (product.effective_price ?? product.price)

      dispatch({ type: 'SET_ITEMS', payload: (() => {
        const same = i => i.product_id === product.id && (i.variant_id ?? null) === variantId
        const existing = state.items.find(same)

        if (existing) {
          return state.items.map(i =>
            same(i)
              ? { ...i, quantity: Math.min(i.quantity + quantity, available) }
              : i
          )
        }

        const qty = Math.min(quantity, available)

        return [...state.items, {
          // Identifiant local, remplacé par celui du serveur à la connexion
          id:            `local-${product.id}-${variantId ?? 'base'}`,
          product_id:    product.id,
          variant_id:    variantId,
          variant_label: extras.variant_label ?? null,
          quantity:      qty,
          unit_price:    unitPrice,
          total_price:   unitPrice * qty,
          product: {
            id:            product.id,
            name:          product.name,
            slug:          product.slug,
            stock:         product.stock,
            primary_image: product.primary_image ?? product.image_url,
          },
        }]
      })() })
    }
    dispatch({ type: 'OPEN_CART' })
  }, [isAuth, state.items])

  const updateQuantity = useCallback(async (itemId, quantity) => {
    // quantity <= 0 : on retire l'article (inline pour éviter la référence circulaire)
    if (quantity < 1) {
      if (isAuth) {
        const data = await cartApi.remove(itemId)
        dispatch({ type: 'SET_ITEMS', payload: liste(data.items, 'panier') })
      } else {
        dispatch({ type: 'SET_ITEMS', payload: state.items.filter(i => i.id !== itemId) })
      }
      return
    }
    if (isAuth) {
      const data = await cartApi.update(itemId, quantity)
      dispatch({ type: 'SET_ITEMS', payload: liste(data.items, 'panier') })
    } else {
      dispatch({ type: 'SET_ITEMS', payload:
        state.items.map(i => i.id === itemId
          ? { ...i, quantity, total_price: i.unit_price * quantity }
          : i
        )
      })
    }
  }, [isAuth, state.items])

  const removeFromCart = useCallback(async (itemId) => {
    if (isAuth) {
      const data = await cartApi.remove(itemId)
      dispatch({ type: 'SET_ITEMS', payload: liste(data.items, 'panier') })
    } else {
      dispatch({ type: 'SET_ITEMS', payload: state.items.filter(i => i.id !== itemId) })
    }
  }, [isAuth, state.items])

  const clearCart = useCallback(async () => {
    if (isAuth) {
      await cartApi.clear()
    }
    dispatch({ type: 'SET_ITEMS', payload: [] })
    lsClear()
  }, [isAuth])

  /* ── Computed ── */
  const itemCount = state.items.reduce((s, i) => s + i.quantity, 0)
  const subtotal  = state.items.reduce((s, i) => s + (i.unit_price * i.quantity), 0)

  return (
    <CartContext.Provider value={{
      error: state.error,
      items:          state.items,
      itemCount,
      subtotal,
      loading:        state.loading,
      syncing:        state.syncing,
      cartOpen:       state.open,
      openCart:       () => dispatch({ type: 'OPEN_CART' }),
      closeCart:      () => dispatch({ type: 'CLOSE_CART' }),
      toggleCart:     () => dispatch({ type: 'TOGGLE_CART' }),
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
    }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
